import { eachUtcDayOfInterval, toEndOfUtcDay, toStartOfUtcDay } from '@chainflip/utils/date';
import { isNotNullish } from '@chainflip/utils/guard';
import { utc, UTCDate } from '@date-fns/utc';
import {
  eachMonthOfInterval,
  eachWeekOfInterval,
  endOfWeek,
  format,
  formatDuration,
  isValid as isValidDate,
  max as maxDate,
  startOfMonth,
  startOfWeek,
  subDays,
} from 'date-fns';
import { FIRST_SWAP_DATE } from '@/shared/constants';
import { type ChartAggregationIntervalOption } from '@/shared/hooks/useChartAggregation';
import { FLIP_SYMBOL } from '@/shared/utils/env';
import TokenAmount from '@/shared/utils/TokenAmount';

// format to number{s} or number{m} or number{h}
export const formatToApproxTime = (durationSeconds: number) => {
  if (durationSeconds < 60) {
    return `${Math.floor(durationSeconds).toFixed()}s`;
  }
  if (durationSeconds < 3600) {
    return `${Math.floor(durationSeconds / 60)}m`;
  }

  const hours = Math.floor(durationSeconds / 3600);
  const minutes = Math.floor((durationSeconds % 3600) / 60);

  return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
};

const MS_PER_MINUTE = 60_000;
const MINUTES_PER_HOUR = 60;
const MINUTES_PER_DAY = 24 * MINUTES_PER_HOUR;

// Compact "1d 23h 53m" countdown until the given date, or null if it has passed.
export const formatCompactCountdown = (target: Date | number): string | null => {
  const totalMinutes = Math.floor((new Date(target).getTime() - Date.now()) / MS_PER_MINUTE);
  if (totalMinutes <= 0) return null;

  const days = Math.floor(totalMinutes / MINUTES_PER_DAY);
  const hours = Math.floor((totalMinutes % MINUTES_PER_DAY) / MINUTES_PER_HOUR);
  const minutes = totalMinutes % MINUTES_PER_HOUR;

  const parts = [];
  if (days) parts.push(`${days}d`);
  if (hours) parts.push(`${hours}h`);
  if (minutes || parts.length === 0) parts.push(`${minutes}m`);

  return parts.join(' ');
};

// a whole number of hours in words, e.g. 168 -> "7 days", 25 -> "1 day 1 hour".
// largestUnitOnly keeps it to a single unit: 25 -> "1 day", 9 -> "9 hours".
export const formatHoursInWords = (hours: number, largestUnitOnly = false) => {
  const days = Math.floor(hours / 24);

  return formatDuration({ days, hours: largestUnitOnly && days > 0 ? 0 : hours % 24 });
};

export const pad = (number: number) => String(number).padStart(2, '0');

export const addToGoogleCalendar = (
  amount: bigint,
  startTimestamp: Date | number,
  endTimestamp: Date | number,
) => {
  const flipAmount = new TokenAmount(amount).toFixed();
  const callbackUrl = process.env.NEXT_PUBLIC_REDEMPTIONS_CALENDAR_CALLBACK_URL;

  const formatDateForCalendar = (date: Date | number) => format(date, "yyyyLLdd'T'HHmmss");
  const start = formatDateForCalendar(startTimestamp);
  const end = formatDateForCalendar(endTimestamp);

  const GoogleCalenderUrl = 'https://calendar.google.com/calendar/render';

  const queryParams = {
    action: 'TEMPLATE',
    dates: `${start}/${end}`,
    text: `Redeem ${flipAmount} ${FLIP_SYMBOL}`,
    details: `Finalise redemption of ${flipAmount} ${FLIP_SYMBOL} from <a href="${callbackUrl}">${callbackUrl}</a>`,
  };

  const url = `${GoogleCalenderUrl}?${new URLSearchParams(queryParams).toString()}`;

  window.open(url, '_blank');
};

// eg: "1h 2m", "expired", "less than a minute"
export const formatTimeUntilExpiry = (duration: number, expiredText = 'Expired') => {
  if (duration <= 0) {
    return expiredText;
  }

  if (duration <= 60) {
    return 'less than a minute';
  }

  const hours = Math.floor(duration / 3600);
  const minutes = Math.floor((duration % 3600) / 60);

  const hoursRepresentation = hours > 0 ? `${hours}h` : '';
  const minutesRepresentation = minutes > 0 ? `${minutes}m` : '';

  return `${hoursRepresentation} ${minutesRepresentation}`;
};

export const getRemainingRetryTime = (fokDuration: number, scheduledAt: number): number =>
  fokDuration * 6 - (Date.now() - scheduledAt) / 1000;

// Date picker utils
export type DatesRange = [startDate: UTCDate, endDate: UTCDate];

export const minSwapDate = new UTCDate(FIRST_SWAP_DATE);

// Convert local date to start of UTC day
export const localDayToUTCDay = (date: Date) =>
  new UTCDate(date.getFullYear(), date.getMonth(), date.getDate());

export const timeframes = [
  {
    name: 'Today',
    startDate: toStartOfUtcDay(new Date()),
    endDate: toEndOfUtcDay(new Date()),
  },
  {
    name: 'Last 2 days',
    startDate: toStartOfUtcDay(subDays(new Date(), 1)),
    endDate: toEndOfUtcDay(new Date()),
  },
  {
    name: 'Last 7 days',
    startDate: toStartOfUtcDay(subDays(new Date(), 6)),
    endDate: toEndOfUtcDay(new Date()),
  },
  {
    name: 'Last 14 days',
    startDate: toStartOfUtcDay(subDays(new Date(), 13)),
    endDate: toEndOfUtcDay(new Date()),
  },
  {
    name: 'Last 30 days',
    startDate: toStartOfUtcDay(subDays(new Date(), 29)),
    endDate: toEndOfUtcDay(new Date()),
  },
  {
    name: 'Last 90 days',
    startDate: toStartOfUtcDay(subDays(new Date(), 89)),
    endDate: toEndOfUtcDay(new Date()),
  },
  {
    name: 'Year to date',
    startDate: new UTCDate(`${new Date().getUTCFullYear()}-01-01T00:00:00Z`),
    endDate: toEndOfUtcDay(new Date()),
  },
  {
    name: 'All time',
    startDate: minSwapDate,
    endDate: toEndOfUtcDay(new Date()),
  },
];

export const getSelectedTimeframe = (
  startDate: UTCDate,
  endDate: UTCDate,
  timeframeList: typeof timeframes = timeframes,
) =>
  timeframeList.find(
    (tf) =>
      tf.startDate.getTime() === startDate.getTime() && tf.endDate.getTime() === endDate.getTime(),
  );

// Date formatting utils
export const getLocaleDateStrings = <T extends Intl.DateTimeFormatOptions>(
  date: Date,
  options: T,
): { [K in keyof T]: string } =>
  Object.fromEntries(
    Object.entries(options).map(([prop, value]) => [
      prop,
      date.toLocaleDateString(undefined, { [prop]: value }),
    ]),
  ) as { [K in keyof T]: string };

export const isSameUtcDay = (a: Date, b: Date) =>
  toStartOfUtcDay(a).getTime() === toStartOfUtcDay(b).getTime();

export const getDatesLabel = (datesRange: DatesRange) => {
  const dateOptions: Required<
    Pick<Intl.DateTimeFormatOptions, 'day' | 'month' | 'year' | 'timeZone'>
  > = {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  };
  const startDateStrings = getLocaleDateStrings(datesRange[0], dateOptions);
  const endDateStrings = getLocaleDateStrings(datesRange[1], dateOptions);

  const endDate = `${endDateStrings.day} ${endDateStrings.month} ${endDateStrings.year}`;
  let startDate: string;

  if (
    datesRange[0].getUTCMonth() === datesRange[1].getUTCMonth() &&
    datesRange[0].getUTCFullYear() === datesRange[1].getUTCFullYear()
  ) {
    startDate = startDateStrings.day;
  } else if (
    datesRange[0].getUTCMonth() !== datesRange[1].getUTCMonth() &&
    datesRange[0].getUTCFullYear() === datesRange[1].getUTCFullYear()
  ) {
    startDate = `${startDateStrings.day} ${startDateStrings.month}`;
  } else {
    startDate = `${startDateStrings.day} ${startDateStrings.month} ${startDateStrings.year}`;
  }

  return { startDate, endDate };
};

export const formatDatesRangeLabel = (datesRange: DatesRange) => {
  const { startDate, endDate } = getDatesLabel(datesRange);
  return isSameUtcDay(...datesRange) ? endDate : `${startDate} - ${endDate}`;
};

export const getTimeframeOrFormattedDateLabels = (datesRange: DatesRange) => {
  const selectedTimeframe = getSelectedTimeframe(...datesRange);
  if (selectedTimeframe) return selectedTimeframe.name;

  return formatDatesRangeLabel(datesRange);
};

export const getTruncatedChartDates = ({
  startDate,
  endDate,
  aggregationInterval,
}: {
  startDate: Date;
  endDate: Date;
  aggregationInterval: ChartAggregationIntervalOption['value'];
}) => {
  const dateRange = {
    start: startDate,
    end: endDate,
  };

  const ensureBoundaries = (dates: Date[]) =>
    dates.map(
      (date) =>
        new Date(Math.min(Math.max(date.getTime(), startDate.getTime()), endDate.getTime())),
    );

  switch (aggregationInterval) {
    case 'W':
      return ensureBoundaries(
        eachWeekOfInterval(dateRange, {
          in: utc,
          weekStartsOn: 1,
        }),
      );
    case 'M':
      return ensureBoundaries(eachMonthOfInterval(dateRange, { in: utc }));
    default:
      return ensureBoundaries(eachUtcDayOfInterval(dateRange));
  }
};

export const getTruncatedDataPointDate = ({
  startDate,
  date,
  aggregationInterval,
}: {
  startDate: Date;
  date: Date;
  aggregationInterval: ChartAggregationIntervalOption['value'];
}) => {
  const startDateTimestamp = startDate.getTime();
  switch (aggregationInterval) {
    case 'W':
      return new Date(
        Math.max(startOfWeek(date, { weekStartsOn: 1, in: utc }).getTime(), startDateTimestamp),
      );
    case 'M':
      return new Date(Math.max(startOfMonth(date, { in: utc }).getTime(), startDateTimestamp));
    default:
      return date;
  }
};

export const getTooltipDateLabel = ({
  date,
  aggregationInterval,
}: {
  date: UTCDate;
  aggregationInterval: ChartAggregationIntervalOption['value'];
}) => {
  let formattedDate;
  if (aggregationInterval === 'W') {
    const endDate = endOfWeek(date, { in: utc, weekStartsOn: 1 });
    const { startDate: timePeriodStart, endDate: timePeriodEnd } = getDatesLabel([date, endDate]);

    return `${timePeriodStart} - ${timePeriodEnd}`;
  }

  if (aggregationInterval === 'M') {
    formattedDate = getLocaleDateStrings(date, {
      month: 'long',
      year: 'numeric',
      timeZone: 'UTC',
    });

    return `${formattedDate.month} ${formattedDate.year}`;
  }

  formattedDate = getLocaleDateStrings(date, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  });

  return `${formattedDate.day} ${formattedDate.month}, ${formattedDate.year}`;
};

// Example date values: [new Date('2023-01-01'), '2023-01-02', 1754561521552, null, undefined]
export const findMaxDate = (
  ...dates: (string | Date | number | null | undefined)[]
): Date | null => {
  const realDates = dates
    .filter((date) => isNotNullish(date) && isValidDate(new Date(date)))
    .map((date) => new Date(date!));
  return realDates.length > 0 ? maxDate(realDates) : null;
};
